import { LOCKED_BASIC_FIELDS } from '@features/forms/constants';
import { FormFieldCategory, FormFieldData } from '@features/forms/types';

import { assignFormFieldDisplayOrders } from './helpers/fieldDisplayOrder';

export const isBasicField = (field: FormFieldData) =>
  field.fieldCategory === 'basic' || field.isLocked === true;

export const getFieldsByCategory = (
  fields: FormFieldData[] | undefined,
  category: FormFieldCategory
) => (fields ?? []).filter((field) => field.fieldCategory === category);

const normalizeBasicFieldLabel = (label: string) =>
  label.trim().toLowerCase().replace(/\s+/g, ' ');

const findExistingBasicField = (
  existing: FormFieldData[],
  template: FormFieldData
) => {
  const normalizedTemplateLabel = normalizeBasicFieldLabel(template.label);

  return (
    existing.find(
      (field) =>
        normalizeBasicFieldLabel(field.label) === normalizedTemplateLabel
    ) ??
    existing.find(
      (field) => isBasicField(field) && field.type === template.type
    )
  );
};

const mergeBasicField = (
  template: FormFieldData,
  existing?: FormFieldData,
  preferTemplateIds = false
): FormFieldData => {
  if (!existing) {
    return template;
  }

  return {
    ...template,
    id: preferTemplateIds ? template.id : existing.id,
    required: existing.required,
    order: existing.order
  };
};

const removeDuplicateBasicFields = (
  mergedBasic: FormFieldData[],
  nonBasic: FormFieldData[]
): FormFieldData[] => {
  const basicLabels = new Set(
    mergedBasic.map((field) => normalizeBasicFieldLabel(field.label))
  );

  return nonBasic.filter(
    (field) => !basicLabels.has(normalizeBasicFieldLabel(field.label))
  );
};

export const ensureWizardFields = (
  fields: FormFieldData[] | undefined,
  options?: { preferTemplateBasicIds?: boolean }
): FormFieldData[] => {
  const existing = fields ?? [];
  const nonBasic = existing.filter((field) => !isBasicField(field));
  const preferTemplateIds = options?.preferTemplateBasicIds ?? false;

  const mergedBasic = LOCKED_BASIC_FIELDS.map((template) =>
    mergeBasicField(
      template,
      findExistingBasicField(existing, template),
      preferTemplateIds
    )
  );

  return assignFormFieldDisplayOrders([
    ...mergedBasic,
    ...removeDuplicateBasicFields(mergedBasic, nonBasic)
  ]);
};

export const hasWizardFieldsChanged = (
  current: FormFieldData[] | undefined,
  next: FormFieldData[]
) => JSON.stringify(current ?? []) !== JSON.stringify(next);
